import { normalizeText } from "./normalize.ts";
import type { ContentTypeId, Floor, Genre } from "@/types/taxonomy";
import type { AudienceType } from "@/types/work";

/**
 * ジャンルの照合・絞り込み・階層化の純粋関数。
 *
 * UIやリポジトリから切り離してあるので、Node のテスト（tests/catalog-rules.test.ts）から
 * そのまま検証できる。ジャンル名は表示用の飾りで、検索条件の復元はIDで行う。
 */

/** 対象モードで表示してよいジャンルか（共通区分を含む） */
export function isGenreVisibleInMode(genre: Genre, mode?: AudienceType): boolean {
  if (!mode) return true;
  return genre.audienceIds.includes(mode) || genre.audienceIds.includes("common");
}

/**
 * ジャンル名・読み・提供元ID・別名の部分一致。
 * normalizeText が全角半角・ひらがなカタカナを吸収する。
 */
export function genreMatchesQuery(genre: Genre, query: string): boolean {
  const key = normalizeText(query);
  if (!key) return true;
  return (
    normalizeText(genre.name).includes(key) ||
    normalizeText(genre.kana).includes(key) ||
    normalizeText(genre.providerGenreId).includes(key) ||
    genre.aliases.some((alias) => normalizeText(alias).includes(key))
  );
}

/**
 * ジャンルID・提供元ID・表示名・読み・別名からジャンルを解決する。
 * 表示名が変わっても、保存済みのジャンルIDはそのまま解決できる。
 */
export function findGenreIn(genres: Genre[], value: string): Genre | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const direct = genres.find((genre) => genre.id === trimmed);
  if (direct) return direct;
  const key = normalizeText(trimmed);
  if (!key) return undefined;
  return genres.find(
    (genre) =>
      normalizeText(genre.providerGenreId) === key ||
      normalizeText(genre.name) === key ||
      normalizeText(genre.kana) === key ||
      genre.aliases.some((alias) => normalizeText(alias) === key),
  );
}

/** 表記揺れを吸収してジャンルIDの配列へ変換する（重複は除去） */
export function resolveGenreIdsIn(genres: Genre[], values: string[]): string[] {
  const resolved: string[] = [];
  values.forEach((value) => {
    if (typeof value !== "string") return;
    const genre = findGenreIn(genres, value);
    if (genre && !resolved.includes(genre.id)) resolved.push(genre.id);
  });
  return resolved;
}

export interface GenreFilterOptions {
  mode?: AudienceType;
  /** 商品形式（ジャンル側に形式の指定が無い場合はすべての形式に属する） */
  contentType?: ContentTypeId | null;
  /** 対象にするフロアID（null は全フロア） */
  floorIds?: string[] | null;
  query?: string;
}

/**
 * モード・商品形式・フロア・検索語でジャンルを絞る。
 * フロアとジャンルの関係はジャンルが保持する floorId だけで判定する
 * （名前の前方一致などで階層を推測しない）。
 */
export function filterGenres(genres: Genre[], options: GenreFilterOptions): Genre[] {
  const floorSet = options.floorIds ? new Set(options.floorIds) : null;
  return genres.filter((genre) => {
    if (!isGenreVisibleInMode(genre, options.mode)) return false;
    if (
      options.contentType &&
      genre.contentTypeIds.length > 0 &&
      !genre.contentTypeIds.includes(options.contentType)
    ) {
      return false;
    }
    if (floorSet && !floorSet.has(genre.floorId)) return false;
    if (options.query && !genreMatchesQuery(genre, options.query)) return false;
    return true;
  });
}

/** フロア順・件数順・名前順で安定して並べる（同点でも並びが揺れない） */
export function sortGenresForList(
  genres: Genre[],
  floors: Floor[],
  counts?: Record<string, number>,
): Genre[] {
  const floorOrder = new Map(floors.map((floor, index) => [floor.id, index]));
  return genres
    .slice()
    .sort((a, b) => {
      const floorDiff = (floorOrder.get(a.floorId) ?? 999) - (floorOrder.get(b.floorId) ?? 999);
      if (floorDiff !== 0) return floorDiff;
      const countDiff = (counts?.[b.id] ?? 0) - (counts?.[a.id] ?? 0);
      if (countDiff !== 0) return countDiff;
      const orderDiff = a.sortOrder - b.sortOrder;
      if (orderDiff !== 0) return orderDiff;
      return a.id.localeCompare(b.id);
    });
}

/**
 * 作品数が多いジャンルを選ぶ（＝「人気」ではなく、実際の作品数に基づく並び）。
 * 作品が0件のジャンルは候補から外す。同件数でも並びが揺れないよう sortOrder / ID で確定させる。
 */
export function pickTopGenres(
  genres: Genre[],
  counts: Record<string, number> | undefined,
  limit = 12,
): Genre[] {
  const max = Math.max(0, Math.floor(limit));
  if (max === 0) return [];
  return genres
    .filter((genre) => (counts?.[genre.id] ?? 0) > 0)
    .slice()
    .sort((a, b) => {
      const countDiff = (counts?.[b.id] ?? 0) - (counts?.[a.id] ?? 0);
      if (countDiff !== 0) return countDiff;
      const orderDiff = a.sortOrder - b.sortOrder;
      if (orderDiff !== 0) return orderDiff;
      return a.id.localeCompare(b.id);
    })
    .slice(0, max);
}

/** フロア（親）→ ジャンル（子）の構造にまとめる */
export function groupGenresByFloor(floors: Floor[], genres: Genre[]): { floor: Floor; genres: Genre[] }[] {
  const byFloor = new Map<string, Genre[]>();
  genres.forEach((genre) => {
    const list = byFloor.get(genre.floorId);
    if (list) list.push(genre);
    else byFloor.set(genre.floorId, [genre]);
  });
  return floors
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((floor) => ({
      floor,
      genres: (byFloor.get(floor.id) ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    }))
    .filter((group) => group.genres.length > 0);
}